import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import type { MyLink } from '../../shared/schemas.ts';
import { Card, Chips } from '../components/ui.tsx';
import { api, config, fail, navigate, savedMode, savedName, toast } from '../lib.ts';

export function HomePage() {
  const links = useSignal<MyLink[] | null>(null);
  useEffect(() => {
    document.title = 'Midway';
    api<MyLink[]>('/api/me').then(
      (l) => {
        links.value = l;
      },
      () => {
        links.value = [];
      },
    );
  }, []);
  return (
    <div class="home">
      <div class="hero">
        <h1>Meet in the middle.</h1>
        <p>Make a link, send it to your friends. Everyone adds where they are, Midway finds fair places, you vote.</p>
      </div>
      <CreateCard />
      {links.value && links.value.length > 0 && (
        <Card>
          <h3>Your links</h3>
          <ul class="link-list">
            {links.value.map((l) => {
              const act = config.value?.activities.find((a) => a.id === l.activity);
              return (
                <li key={l.id}>
                  <a class="link-row" href={`/m/${l.id}`}>
                    <span>{act?.emoji ?? '📍'}</span>
                    <span class="grow">
                      <b>{l.title}</b>
                      <span class="small muted">
                        {' '}
                        · {l.members} {l.members === 1 ? 'person' : 'people'}
                        {l.winner ? ` · 🎉 ${l.winner}` : ''}
                      </span>
                    </span>
                    <span class="chev">›</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

function CreateCard() {
  const name = useSignal(savedName.get());
  const title = useSignal('');
  const activity = useSignal<string>('restaurant');
  const busy = useSignal(false);
  const acts = [...config.value!.activities, { id: 'none', label: 'Only our own ideas', emoji: '✋' }];
  const submit = async (e: Event) => {
    e.preventDefault();
    if (!name.value.trim()) return toast('Add your name');
    busy.value = true;
    try {
      savedName.set(name.value.trim());
      const { id } = await api<{ id: string }>('/api/meetups', {
        method: 'POST',
        body: {
          name: name.value.trim(),
          title: title.value.trim() || undefined,
          activity: activity.value === 'none' ? null : activity.value,
          travelMode: savedMode.get() ?? undefined,
        },
      });
      navigate(`/m/${id}`);
    } catch (err) {
      fail(err);
      busy.value = false;
    }
  };
  return (
    <Card>
      <form class="stack" onSubmit={submit}>
        <h2>New link</h2>
        <input
          type="text"
          placeholder="Your name"
          maxLength={40}
          value={name.value}
          onInput={(e) => (name.value = e.currentTarget.value)}
        />
        <input
          type="text"
          placeholder="What's it for? (optional, e.g. Friday dinner)"
          maxLength={80}
          value={title.value}
          onInput={(e) => (title.value = e.currentTarget.value)}
        />
        <Chips options={acts} value={activity.value} onChange={(v) => (activity.value = v)} small />
        <button class="btn primary big" type="submit" disabled={busy.value}>
          Create link
        </button>
        <span class="hint">The link stays the same, so your group can use it again next time.</span>
      </form>
    </Card>
  );
}
